import { useState } from "react";

import { useSearchParams } from "@remix-run/react";
import clsx from "clsx";
import { format } from "date-fns";
import { Calendar as CalendarIcon } from "lucide-react";

import { Button } from "~/components/ui/button";
import { Calendar } from "~/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "~/components/ui/popover";
import { useSession, useSessionMutation } from "~/hooks/use-session";

export const DateEditor: React.FC<{
    sessionId: string;
    currentDate: Date;
    prefix?: string;
    // AIDEV-NOTE: `eyebrow` renders the date as the Session Notes redesign eyebrow
    // (mockup 2): small uppercase terra-700 line, tracked, centered above the title.
    eyebrow?: boolean;
}> = ({ sessionId, currentDate, prefix = "", eyebrow = false }) => {
    const [date, setDate] = useState<Date>(currentDate);
    const [open, setOpen] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const { session, isConsultantViewer } = useSession(sessionId);
    const [searchParams] = useSearchParams();
    const sessionToken = searchParams.get("sessionToken");
    // AIDEV-NOTE: Self-contained, like TitleEditor — must detect a consultant
    // viewer itself since callers only pass sessionId/currentDate (Adam,
    // 2026-09-03).
    const isPublicSession = !!sessionToken || isConsultantViewer;

    const { mutate: updateSession, isPending } = useSessionMutation(
        sessionId,
        session?.uploadedByUserId,
        sessionToken,
    );

    const handleSave = () => {
        /* Brent: Some special handling of dates is needed to convert from the user's local timezone to UTC (for the database)*/

        // Create a new Date object representing midnight UTC for the selected date
        const utcMidnightDate = new Date(
            Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()),
        );

        // Check if the *UTC midnight representation* is different from the *currentDate's UTC midnight representation*
        const currentUtcMidnightTime = Date.UTC(
            currentDate.getFullYear(),
            currentDate.getMonth(),
            currentDate.getDate(),
        );

        if (utcMidnightDate.getTime() !== currentUtcMidnightTime) {
            updateSession({
                action: "update-date",
                date: utcMidnightDate, // Send the adjusted UTC midnight date
            });
        }
        setOpen(false);
        setIsEditing(false);
    };

    const handleCancel = () => {
        setDate(currentDate);
        setOpen(false);
        setIsEditing(false);
    };

    return (
        <div className="flex justify-start">
            <Popover open={open} onOpenChange={setOpen}>
                <PopoverTrigger asChild>
                    <Button
                        variant="ghost"
                        size="sm"
                        className={clsx(
                            eyebrow
                                ? "h-auto px-0 py-0.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-brand-terra-700 hover:bg-transparent hover:text-brand-terra-700 print:text-[11px]"
                                : "text-md font-thinner text-brand-forest-800 hover:text-brand-forest-800 hover:bg-transparent print:text-[13px]",
                            isEditing &&
                                "bg-brand-cream-100/30 border border-brand-forest-800",
                        )}
                        onClick={() => !isPublicSession && setIsEditing(true)}
                    >
                        {prefix}
                        {format(date, "MMMM d")}
                        {isEditing && <CalendarIcon className="ml-2 size-4" />}
                    </Button>
                </PopoverTrigger>
                {isEditing && (
                    <PopoverContent className="w-auto p-0" align="center">
                        <Calendar
                            mode="single"
                            selected={date}
                            onSelect={(date) => date && setDate(date)}
                            initialFocus
                        />
                        <div className="flex justify-end gap-2 border-t p-2">
                            <Button
                                size="sm"
                                variant="ghost"
                                onClick={handleCancel}
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={handleSave}
                                disabled={isPending}
                            >
                                {isPending ? "Saving..." : "Save"}
                            </Button>
                        </div>
                    </PopoverContent>
                )}
            </Popover>
        </div>
    );
};
